import React from 'react';
import { useTranslation } from 'react-i18next';
import PhoneVerification from '../PhoneVerification';
import IdentityVerification from '../IdentityVerification';

export interface PersonalInfoSectionProps {
  name: string;
  nickname: string;
  phoneNumber: string;
  phoneVerified: boolean;
  onChange: (field: string, value: string | boolean) => void;
  errors: Record<string, string>;
  isPhoneExisting?: boolean;
  hidePhoneVerification?: boolean;
  /**
   * Render the 휴대폰 본인확인 (NICE) control instead of the SMS OTP button.
   * NICE proves identity as well as possession, so where it is available it
   * supersedes the OTP flow.
   */
  showIdentityVerification?: boolean;
  /**
   * Drives the 본인확인 badge and locks the name field: once NICE has attested a
   * 실명, the name is no longer the user's to type.
   */
  identityVerified?: boolean;
  onIdentityVerified?: (result: { identityName?: string; phoneNumber?: string }) => void;
}

/**
 * 개인정보 section — MatchGo "Bright" styling.
 * Row layout: a 120px label column + field column (collapses to one column
 * on mobile). Section header uses the navy underline accent.
 */
const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  name,
  nickname,
  phoneNumber,
  phoneVerified,
  onChange,
  errors,
  isPhoneExisting = false,
  hidePhoneVerification = false,
  showIdentityVerification = false,
  identityVerified = false,
  onIdentityVerified,
}) => {
  const { t } = useTranslation('profile');

  return (
    <section className="px-6 sm:px-8 py-7">
      <div className="flex items-center justify-between gap-2 pb-3.5 mb-2 border-b-2 border-[#0F1E3D]">
        <h2 className="text-[18px] font-bold tracking-tight text-[#0F1E3D]">
          {t('myProfile.form.personalInfo')}
        </h2>
        <span className="font-mono text-[10px] font-semibold text-[#6B7794] bg-[#F7F8FB] border border-[#E4E8F0] rounded px-2 py-0.5">
          {t('myProfile.form.savedTag', '저장 버튼 대상')}
        </span>
      </div>

      {/* Name (required) */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4 border-b border-[#EEF1F6]">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2.5 flex items-center gap-1">
          {t('myProfile.form.name')} <span className="text-[#FF6B6B]">*</span>
        </div>
        <div>
          {/* Locked once 본인확인 has attested a 실명 — at that point the value is
              NICE's, not the user's, and letting it be edited would leave the
              displayed name disagreeing with the verified one. Editable before
              verification because `name` is required and would otherwise be
              impossible to fill. */}
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => onChange('name', e.target.value)}
            readOnly={identityVerified}
            aria-readonly={identityVerified}
            placeholder={t('myProfile.form.namePlaceholder')}
            className={`w-full max-w-[320px] px-3.5 py-2.5 text-sm rounded-lg border-[1.5px] transition focus:outline-none ${
              identityVerified
                ? 'border-[#E4E8F0] bg-[#F7F8FB] text-[#6B7794] cursor-not-allowed'
                : errors.name
                  ? 'border-[#F25151] bg-white text-[#0F1E3D] focus:border-[#F25151] focus:ring-2 focus:ring-[#FFE4E4]'
                  : 'border-[#E4E8F0] bg-white text-[#0F1E3D] focus:border-[#2D5BFF] focus:ring-2 focus:ring-[#E6EDFF]'
            }`}
          />
          {identityVerified ? (
            <p className="mt-1.5 text-xs text-[#6B7794]">
              {t('myProfile.form.nameFromIdentity', '본인인증으로 확인된 이름입니다.')}
            </p>
          ) : (
            errors.name && <p className="mt-1.5 text-xs text-[#F25151]">{errors.name}</p>
          )}
        </div>
      </div>

      {/* Nickname (optional) */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4 border-b border-[#EEF1F6]">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2.5">
          {t('myProfile.form.nickname')}
        </div>
        <div>
          <input
            id="nickname"
            type="text"
            value={nickname}
            onChange={(e) => onChange('nickname', e.target.value)}
            placeholder={t('myProfile.form.nicknamePlaceholder')}
            className="w-full max-w-[320px] px-3.5 py-2.5 text-sm rounded-lg border-[1.5px] border-[#E4E8F0] bg-white text-[#0F1E3D] transition focus:outline-none focus:border-[#2D5BFF] focus:ring-2 focus:ring-[#E6EDFF]"
          />
        </div>
      </div>

      {/* Phone Number + Phone Verification */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2.5 flex items-center gap-1">
          {t('myProfile.form.phoneNumber')}
          <span className="relative group inline-flex">
            <span className="cursor-help font-mono text-[10px] w-[15px] h-[15px] rounded-full border-[1.4px] border-[#A0AAC2] text-[#6B7794] flex items-center justify-center">i</span>
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 px-3 py-2 text-xs font-normal text-white bg-[#0F1E3D] rounded-lg shadow-lg z-10 leading-relaxed">
              {t('myProfile.form.phoneTooltip')}
            </span>
          </span>
        </div>
        <div>
          <PhoneVerification
            phoneNumber={phoneNumber}
            isVerified={phoneVerified}
            onPhoneChange={(phone) => onChange('phoneNumber', phone)}
            isExisting={isPhoneExisting}
            hideChangeButton={hidePhoneVerification}
          />
          {errors.phoneNumber && <p className="mt-1.5 text-xs text-[#F25151]">{errors.phoneNumber}</p>}
          {/* Dropped once verified so the wrapper's margin does not linger as a
              gap below the phone row — IdentityVerification renders nothing in
              that state, since PhoneVerification already shows the 인증완료 chip. */}
          {showIdentityVerification && !identityVerified && (
            <div className="mt-3">
              <IdentityVerification
                isVerified={identityVerified}
                onVerified={(result) => onIdentityVerified?.(result)}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PersonalInfoSection;
